import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Loader2, ArrowUpCircle, Search, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABELS = {
  naik: { label: "Naik Kelas", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  tetap: { label: "Tinggal Kelas", color: "bg-amber-100 text-amber-700 border-amber-300" },
  lulus: { label: "Lulus", color: "bg-sky-100 text-sky-700 border-sky-300" },
  nonaktif: { label: "Keluar/Nonaktif", color: "bg-slate-200 text-slate-700 border-slate-300" },
};

export default function KenaikanKelasPage() {
  const [tahunList, setTahunList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [tahunLama, setTahunLama] = useState("");
  const [tahunBaru, setTahunBaru] = useState("");

  const [rows, setRows] = useState([]);
  const [entries, setEntries] = useState({}); // santri_id -> {action, kelas_id_baru, catatan}
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterKelas, setFilterKelas] = useState("all");
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Bootstrap selects
  useEffect(() => {
    (async () => {
      try {
        const [taRes, klRes] = await Promise.all([
          api.get("/tahun-ajaran"),
          api.get("/kelas"),
        ]);
        const tas = taRes.data.items || [];
        setTahunList(tas);
        setKelasList(klRes.data.items || []);
        const aktif = tas.find((t) => t.is_active);
        if (aktif) setTahunLama(aktif.tahun_ajaran);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, []);

  const loadPreview = useCallback(async () => {
    if (!tahunLama) return;
    setLoading(true);
    try {
      const { data } = await api.get("/kenaikan-kelas/preview", {
        params: { tahun_ajaran_lama: tahunLama },
      });
      const items = data.items || [];
      setRows(items);
      // default action: "naik", default kelas_id_baru = current kelas
      const next = {};
      for (const r of items) {
        next[r.santri_id] = {
          action: "naik",
          kelas_id_baru: r.kelas_id_lama || "",
          catatan: "",
        };
      }
      setEntries(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tahunLama]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const updateEntry = (santriId, patch) => {
    setEntries((prev) => ({ ...prev, [santriId]: { ...prev[santriId], ...patch } }));
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterKelas !== "all" && r.kelas_id_lama !== filterKelas) return false;
      if (!q) return true;
      return (
        (r.nama || "").toLowerCase().includes(q) ||
        (r.nis || "").toLowerCase().includes(q)
      );
    });
  }, [rows, filterKelas, search]);

  const counters = useMemo(() => {
    const acc = { naik: 0, tetap: 0, lulus: 0, nonaktif: 0 };
    for (const r of rows) {
      const a = entries[r.santri_id]?.action || "naik";
      acc[a] = (acc[a] || 0) + 1;
    }
    return acc;
  }, [entries, rows]);

  const validate = () => {
    if (!tahunLama) return "Pilih tahun ajaran asal.";
    if (!tahunBaru) return "Isi tahun ajaran tujuan.";
    if (tahunBaru.trim() === tahunLama.trim()) return "Tahun ajaran tujuan harus berbeda dari tahun ajaran asal.";
    for (const r of rows) {
      const e = entries[r.santri_id];
      if (!e) continue;
      if (e.action === "naik" && !e.kelas_id_baru) {
        return `Pilih kelas baru untuk santri ${r.nama}.`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        tahun_ajaran_lama: tahunLama,
        tahun_ajaran_baru: tahunBaru,
        entries: rows.map((r) => {
          const e = entries[r.santri_id] || { action: "naik" };
          return {
            santri_id: r.santri_id,
            action: e.action,
            kelas_id_baru: e.action === "naik" ? e.kelas_id_baru : null,
            catatan: e.catatan || null,
          };
        }),
      };
      const { data } = await api.post("/kenaikan-kelas/process", payload);
      toast.success(`Proses selesai: ${data.processed} santri diperbarui${data.skipped ? `, ${data.skipped} dilewati` : ""}.`);
      setConfirmOpen(false);
      await loadPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const kelasNama = (id) => kelasList.find((k) => k.id === id)?.nama || "-";

  return (
    <div className="space-y-6" data-testid="kenaikan-kelas-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ArrowUpCircle className="h-7 w-7 text-emerald-600" />
            Kenaikan Kelas
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Proses kenaikan kelas santri ke tahun ajaran baru. Setiap perubahan disimpan ke riwayat kelas
            santri. Tidak ada data lama yang dihapus.
          </p>
        </div>
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1.5 py-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Hanya Admin
        </Badge>
      </header>

      <Card className="p-5 space-y-4 border-slate-200">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Tahun Ajaran Asal</Label>
            <Select value={tahunLama} onValueChange={setTahunLama}>
              <SelectTrigger data-testid="select-tahun-lama" className="mt-1.5">
                <SelectValue placeholder="Pilih tahun ajaran asal" />
              </SelectTrigger>
              <SelectContent>
                {tahunList.map((t) => (
                  <SelectItem key={t.id} value={t.tahun_ajaran}>
                    {t.tahun_ajaran} {t.is_active ? " (Aktif)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Tahun Ajaran Tujuan</Label>
            <Input
              data-testid="input-tahun-baru"
              className="mt-1.5"
              placeholder="contoh: 2026/2027"
              value={tahunBaru}
              onChange={(e) => setTahunBaru(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wider">Filter Kelas Asal</Label>
            <Select value={filterKelas} onValueChange={setFilterKelas}>
              <SelectTrigger data-testid="select-filter-kelas" className="mt-1.5">
                <SelectValue placeholder="Semua kelas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua kelas</SelectItem>
                {kelasList.map((k) => (
                  <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid="input-search-santri"
              className="pl-9"
              placeholder="Cari nama atau NIS..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2 text-xs">
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">Naik: {counters.naik}</Badge>
            <Badge className="bg-amber-100 text-amber-700 border border-amber-300">Tinggal: {counters.tetap}</Badge>
            <Badge className="bg-sky-100 text-sky-700 border border-sky-300">Lulus: {counters.lulus}</Badge>
            <Badge className="bg-slate-200 text-slate-700 border border-slate-300">Nonaktif: {counters.nonaktif}</Badge>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-12">No</TableHead>
                <TableHead>NIS</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Kelas Lama</TableHead>
                <TableHead className="min-w-[140px]">Tindakan</TableHead>
                <TableHead className="min-w-[180px]">Kelas Baru</TableHead>
                <TableHead className="min-w-[200px]">Catatan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-emerald-600 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    Tidak ada santri aktif untuk diproses.
                  </TableCell>
                </TableRow>
              ) : filteredRows.map((r, idx) => {
                const e = entries[r.santri_id] || { action: "naik", kelas_id_baru: r.kelas_id_lama, catatan: "" };
                return (
                  <TableRow key={r.santri_id} data-testid={`row-santri-${r.santri_id}`}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{r.nis || "-"}</TableCell>
                    <TableCell className="font-medium text-slate-900">{r.nama}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-700">{r.kelas_nama_lama}</span>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={e.action}
                        onValueChange={(v) => updateEntry(r.santri_id, { action: v })}
                      >
                        <SelectTrigger data-testid={`action-${r.santri_id}`} className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="naik">Naik Kelas</SelectItem>
                          <SelectItem value="tetap">Tinggal Kelas</SelectItem>
                          <SelectItem value="lulus">Lulus</SelectItem>
                          <SelectItem value="nonaktif">Keluar / Nonaktif</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {e.action === "naik" ? (
                        <Select
                          value={e.kelas_id_baru || ""}
                          onValueChange={(v) => updateEntry(r.santri_id, { kelas_id_baru: v })}
                        >
                          <SelectTrigger data-testid={`kelas-baru-${r.santri_id}`} className="h-9">
                            <SelectValue placeholder="Pilih kelas baru" />
                          </SelectTrigger>
                          <SelectContent>
                            {kelasList.map((k) => (
                              <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Textarea
                        data-testid={`catatan-${r.santri_id}`}
                        rows={1}
                        className="min-h-[36px] text-xs"
                        placeholder="(opsional)"
                        value={e.catatan || ""}
                        onChange={(ev) => updateEntry(r.santri_id, { catatan: ev.target.value })}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 sticky bottom-0 bg-white border-t border-slate-200 -mx-4 md:-mx-8 px-4 md:px-8 py-3 shadow-lg">
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <History className="h-4 w-4 text-slate-400" />
          Total {rows.length} santri akan diproses ke tahun ajaran <strong className="text-slate-900">{tahunBaru || "—"}</strong>.
        </div>
        <Button
          data-testid="btn-process-kenaikan"
          disabled={rows.length === 0 || submitting}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={() => {
            const err = validate();
            if (err) { toast.error(err); return; }
            setConfirmOpen(true);
          }}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowUpCircle className="h-4 w-4 mr-2" />}
          Proses Kenaikan Kelas
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Kenaikan Kelas</AlertDialogTitle>
            <AlertDialogDescription>
              Anda akan memproses <strong>{rows.length}</strong> santri dari tahun ajaran
              <strong> {tahunLama}</strong> ke <strong>{tahunBaru}</strong>. Riwayat akan disimpan
              dan data lama tetap dipertahankan. Lanjutkan?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-sm text-slate-700 grid grid-cols-2 gap-2">
            <div>Naik Kelas: <strong>{counters.naik}</strong></div>
            <div>Tinggal Kelas: <strong>{counters.tetap}</strong></div>
            <div>Lulus: <strong>{counters.lulus}</strong></div>
            <div>Nonaktif: <strong>{counters.nonaktif}</strong></div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-confirm-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-process"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={(ev) => { ev.preventDefault(); handleSubmit(); }}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Ya, Proses Sekarang
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
